import { useState, useEffect } from "react";
import { Cookie, X, Check, Shield } from "lucide-react";
import { Button } from "./ui/button";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else if (consent === "accepted") {
      // Load Google Analytics if previously accepted
      loadGoogleAnalytics();
    }
  }, []);

  const loadGoogleAnalytics = () => {
    // Check if already loaded
    if (window.gtag) return;

    // Load gtag.js script
    const script = document.createElement("script");
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-BRMNLT0TRD";
    script.async = true;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    gtag("js", new Date());
    gtag("config", "G-BRMNLT0TRD");
  };

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "accepted");
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    loadGoogleAnalytics();
    setShowBanner(false);
  };

  const handleReject = () => {
    localStorage.setItem("cookieConsent", "rejected");
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    setShowBanner(false);
  };

  const handleAcceptEssential = () => {
    localStorage.setItem("cookieConsent", "essential");
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay for mobile */}
      <div 
        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[998] md:hidden"
        onClick={() => {}}
      />
      
      {/* Cookie Banner */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-[999] p-4 md:p-6"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          {/* Main Banner */}
          <div className="p-5 md:p-6">
            <div className="flex items-start gap-4">
              {/* Cookie Icon */}
              <div className="hidden md:flex w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-amber-600" />
              </div>
              
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800 mb-2 flex items-center gap-2">
                  <Cookie className="w-5 h-5 text-amber-600 md:hidden" />
                  We value your privacy
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                  You can choose to accept all cookies, only essential cookies, or reject non-essential cookies.
                </p>
                
                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <Button
                    onClick={handleAccept}
                    className="h-10 px-5 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-medium hover:opacity-90 flex items-center justify-center gap-2"
                    data-testid="cookie-accept-all-btn"
                  >
                    <Check className="w-4 h-4" />
                    Accept All
                  </Button>
                  
                  <Button
                    onClick={handleAcceptEssential}
                    variant="outline"
                    className="h-10 px-5 rounded-full border-slate-300 text-slate-700 font-medium hover:bg-slate-50 flex items-center justify-center gap-2"
                    data-testid="cookie-essential-btn"
                  >
                    <Shield className="w-4 h-4" />
                    Essential Only
                  </Button>
                  
                  <Button
                    onClick={handleReject}
                    variant="ghost"
                    className="h-10 px-5 rounded-full text-slate-500 font-medium hover:bg-slate-100 flex items-center justify-center gap-2"
                    data-testid="cookie-reject-btn"
                  >
                    <X className="w-4 h-4" />
                    Reject All
                  </Button>
                  
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="text-sm text-violet-600 font-medium hover:underline sm:ml-auto"
                    data-testid="cookie-details-btn"
                  >
                    {showDetails ? "Hide details" : "Cookie details"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Cookie Details (Expandable) */}
          {showDetails && (
            <div className="border-t border-slate-100 bg-slate-50 p-5 md:p-6">
              <h4 className="font-semibold text-slate-800 mb-4">Cookie Categories</h4>
              <div className="space-y-4">
                {/* Essential Cookies */}
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-100">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h5 className="font-medium text-slate-800">Essential Cookies</h5>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Always Active</span>
                    </div>
                    <p className="text-sm text-slate-600">
                      Required for the website to function properly. These include cookies for authentication, 
                      security, and remembering your preferences.
                    </p>
                  </div>
                </div>
                
                {/* Analytics Cookies */}
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3v18h18" />
                      <path d="M18 9l-5 5-4-4-3 3" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h5 className="font-medium text-slate-800">Analytics Cookies</h5>
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Optional</span>
                    </div>
                    <p className="text-sm text-slate-600">
                      Help us understand how visitors interact with our website by collecting anonymous information. 
                      We use Google Analytics to analyze traffic and improve our service.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-500">
                  For more information, please read our{" "}
                  <a href="/privacy" className="text-violet-600 hover:underline">Privacy Policy</a>
                  {" "}and{" "}
                  <a href="/cookies" className="text-violet-600 hover:underline">Cookie Policy</a>.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
